import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

// Product configuration - must match backend
const PRODUCTS: Record<string, { name: string; price: number; price2person: number; description: string; type?: string }> = {
  '606': { name: 'Minor Child-Centered Estate Plan', price: 199, price2person: 299, description: 'Create a will-based plan to appoint a Tutor for minor children.' },
  '614': { name: 'Power of Attorney Supplement', price: 99, price2person: 149, description: 'Financial and Healthcare Power of Attorney documents.' },
  '673': { name: 'Will-Based Estate Plan', price: 199, price2person: 299, description: 'Control your legacy with a comprehensive will-based estate plan.' },
  '676': { name: 'Trust-Based Estate Plan', price: 399, price2person: 599, description: 'Avoid probate and transfer assets smoothly with a trust.' },
  '1367': {
    name: 'Legal Edge Plan',
    price: 9.99,
    price2person: 9.99,
    description: 'Forever revisions and Advanced Estate Plan upgrade credit. Cancel anytime.',
    type: 'subscription',
  },
};

// Easter egg: Type "geaux" to enable test mode
const EASTER_EGG_CODE = 'geaux';

// Maintenance mode - set to true to block new enrollments during upgrades
const MAINTENANCE_MODE = false;

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { cart, addEstatePlan, applyReferral, removeReferral } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [keySequence, setKeySequence] = useState('');
  const [refInput, setRefInput] = useState('');
  const addAttempted = useRef(false);
  const refApplied = useRef(false);

  const returnUrl = '/checkout';
  const hasItems = cart.items.length > 0;
  const firstItem = cart.items[0];

  const productParam = searchParams.get('product');
  const hasValidProduct = !!productParam && !!PRODUCTS[productParam];

  // When the cart is empty, either seed it from a valid ?product= (so the quiz
  // recommendation and direct links land on a populated checkout) or, if there
  // is no product to fall back on, bounce to the shop.
  useEffect(() => {
    if (hasItems || addAttempted.current) return;

    if (hasValidProduct) {
      addAttempted.current = true;
      // Everyone buys the solo plan at checkout now — a second person is a paid
      // add-on inside the interview. The ?type= param is ignored for pricing so
      // an old ?type=2person link can no longer put a couple's plan in the cart.
      addEstatePlan(Number(productParam), 'solo', false);
    } else {
      navigate('/shop');
    }
  }, [hasItems, hasValidProduct, productParam, addEstatePlan, navigate]);

  // Auto-apply a referral code from the URL (?ref=CODE) — the link an attorney
  // shares with a client. Waits until the cart has items, runs once.
  useEffect(() => {
    if (refApplied.current) return;
    const ref = searchParams.get('ref');
    if (!ref || cart.items.length === 0) return;
    refApplied.current = true;
    applyReferral(ref);
  }, [searchParams, cart.items.length, applyReferral]);

  // Easter egg: Listen for key sequence "geaux" to enable test mode
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    const newSequence = (keySequence + key).slice(-EASTER_EGG_CODE.length);
    setKeySequence(newSequence);

    if (newSequence === EASTER_EGG_CODE) {
      setTestMode(true);
      setKeySequence('');
    }
  }, [keySequence]);

  useEffect(() => {
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, [handleKeyPress]);

  // Also check for ?test=geaux in URL
  useEffect(() => {
    if (searchParams.get('test') === 'geaux') {
      setTestMode(true);
    }
  }, [searchParams]);

  // Resolve the first cart item's product ID + variant to the concrete form_type
  // the interview and backend use (test bypass).
  const getFormTarget = () => {
    const productId = String(firstItem.productId);
    const formType = firstItem.variationId === 2 ? '2person' : 'solo';
    const formTypeMap: Record<string, Record<string, string>> = {
      '614': { solo: 'powerOfAttorneyForm', '2person': 'powerOfAttorneyForm2Person' },
      '676': { solo: 'trustBasedEstatePlanSolo', '2person': 'trustBasedEstatePlan2Person' },
      '673': { solo: 'willBasedEstatePlan', '2person': 'willBasedEstatePlan2Person' },
      '606': { solo: 'minorChildEstatePlan', '2person': 'minorChildEstatePlan2Person' },
    };
    const productMap = formTypeMap[productId] || formTypeMap['614'];
    const mappedType = productMap[formType] || productMap.solo || 'powerOfAttorneyForm';
    return { productId, mappedType };
  };

  // Business items are custom cart entries (productId 0), billed by SKU code:
  // LLC / operating-agreement items carry it in metadata.packageType; the
  // registered-agent item has no package code, so its cart `type` is the sku.
  const businessSku = (i: typeof cart.items[number]) => (
    i.type === 'registered-agent'
      ? 'registered-agent'
      : (i.metadata?.packageType as string | undefined)
  );
  const businessItems = cart.items.filter((i) => i.productId === 0);

  // Test-mode bypass for a business cart: open the company the Stripe webhook
  // would open for this cart (same tier, same LLC name) and go straight to its
  // interview. A business set always has an owner, so this needs a login.
  const handleBusinessBypass = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent('/checkout?test=geaux')}`);
      return;
    }
    setError('');
    try {
      const response = await api.post<{ id: number }>('/business-plans/test-bypass', {
        skus: businessItems.map(businessSku).filter(Boolean),
        llcName: (businessItems.find((i) => i.metadata?.llcName)?.metadata?.llcName as string | undefined) || '',
      });
      if (response.success && response.data?.id) {
        navigate(`/business-plan/${response.data.id}`);
        return;
      }
      setError(response.error || 'Could not open the test company.');
    } catch {
      setError('Could not open the test company.');
    }
  };

  // Bypass checkout and go directly to form (test mode).
  //
  // To mirror a real purchase, pre-create the blank "set" the Stripe webhook
  // would have created (same shape POAForm saves), so it lands on the dashboard
  // with its GP-###### number and the form opens that specific set. This needs a
  // signed-in user to own the row; signed out (or if the create fails) we fall
  // back to opening a fresh form with no pre-made set. A business cart goes to
  // the company interview instead; it has no estate form to open.
  const handleTestBypass = async () => {
    if (!firstItem) {
      navigate('/');
      return;
    }
    if (businessItems.length > 0) {
      await handleBusinessBypass();
      return;
    }
    const { productId, mappedType } = getFormTarget();

    if (isAuthenticated) {
      try {
        const response = await api.post<{ id: number }>('/submissions', {
          form_data: {},
          form_type: mappedType,
          submission_status: 'inprogress',
        });
        if (response.success && response.data?.id) {
          navigate(`/poa-form?product=${productId}&type=${mappedType}&submission=${response.data.id}`);
          return;
        }
      } catch {
        // fall through to the plain bypass below
      }
    }

    navigate(`/poa-form?product=${productId}&type=${mappedType}`);
  };

  const handleCheckout = async () => {
    setIsLoading(true);
    setError('');

    try {
      const items = cart.items.map(i => {
        // The backend bills and tags business items by SKU; llcName titles the
        // business set the webhook opens for this company in My Companies.
        if (i.productId === 0) {
          return { productId: 0, sku: businessSku(i), llcName: (i.metadata?.llcName as string | undefined) || '' };
        }
        return {
          productId: i.productId,
          formType: i.variationId === 2 ? '2person' : 'solo',
        };
      });

      // The code is sent, not the discount. The backend re-validates it against
      // its own coupon table and computes the reduction itself, so an edited
      // localStorage cart cannot buy anything cheaply.
      const response = await api.post<{ sessionId: string; url: string }>(
        '/stripe/create-checkout-session',
        { items, couponCode: cart.coupon?.code, referralCode: cart.referral?.code }
      );

      if (response.success && response.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        setError(response.error || 'Failed to create checkout session');
        setIsLoading(false);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  if (!hasItems) {
    // While seeding the cart from a valid ?product= param, show a brief holding
    // state instead of returning null so there is no flash of the /shop bounce.
    if (hasValidProduct) {
      return (
        <main>
          <section className="plans-section">
            <div className="container" style={{ textAlign: 'center', padding: '80px 20px' }}>
              <p style={{ color: '#707070', fontSize: '18px' }}>Preparing your order...</p>
            </div>
          </section>
        </main>
      );
    }
    return null;
  }

  return (
    <main>
      <section className="plans-section">
        <div className="container">
          <div
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              padding: 'clamp(20px, 5vw, 40px)',
            }}
          >
            <h1 style={{ textAlign: 'center', marginBottom: '10px' }}>Checkout</h1>
            <p style={{ textAlign: 'center', color: '#707070', marginBottom: '40px' }}>
              Review your order and proceed to payment
            </p>

            {/* Maintenance Mode Banner */}
            {MAINTENANCE_MODE && !testMode && (
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #ffc107',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>🚧</div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#856404' }}>
                  Temporarily Unavailable
                </h3>
                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>
                  We apologize for the inconvenience. We're currently making improvements to GeauxPlans,
                  and new enrollments are temporarily paused. Thank you for your understanding.
                </p>
              </div>
            )}

            {/* Test Mode Banner - Easter Egg */}
            {testMode && (
              <div
                style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '2px dashed #ffc107',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '20px', marginBottom: '10px' }}>🧪 TEST MODE ENABLED 🧪</div>
                <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>
                  {businessItems.length > 0
                    ? 'Bypass payment and open this company in My Companies for testing.'
                    : 'Bypass payment and go directly to the form for testing.'}
                </p>
                <button
                  onClick={handleTestBypass}
                  className="btn"
                  style={{
                    backgroundColor: '#ffc107',
                    color: '#000',
                    padding: '12px 30px',
                    fontWeight: 'bold',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  🚀 Skip to Form (Test)
                </button>
              </div>
            )}

            {error && (
              <div
                style={{
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  padding: '12px 15px',
                  borderRadius: '4px',
                  marginBottom: '20px',
                  border: '1px solid #f5c6cb',
                }}
              >
                {error}
              </div>
            )}

            {/* Order Summary */}
            <div
              style={{
                border: '1px solid #eaeaea',
                borderRadius: '8px',
                padding: '30px',
                marginBottom: '30px',
                backgroundColor: '#fff',
              }}
            >
              <h2 style={{ marginBottom: '20px', fontSize: '20px' }}>Order Summary</h2>

              {cart.items.map((item) => {
                const productMeta = PRODUCTS[String(item.productId)];
                const isSub = item.type === 'subscription' || productMeta?.type === 'subscription';
                const isMarried = item.variationId === 2;
                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '12px',
                      paddingBottom: '20px',
                      borderBottom: '1px solid #eaeaea',
                      marginBottom: '20px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontSize: '18px', marginBottom: '5px' }}>{item.name}</h3>
                      {productMeta?.description && (
                        <p style={{ color: '#707070', fontSize: '14px', margin: 0 }}>
                          {productMeta.description}
                        </p>
                      )}
                      {isMarried && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: '10px',
                            padding: '4px 10px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            borderRadius: '4px',
                            fontSize: '12px',
                          }}
                        >
                          Married Couple Plan
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#004d71', whiteSpace: 'nowrap' }}>
                      ${item.price.toFixed(2)}{isSub ? '/mo' : ''}
                    </span>
                  </div>
                );
              })}

              {/* Shown so the total does not silently differ from the sum of
                  the lines above it. */}
              {cart.discount && (cart.coupon || cart.referral) ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '15px',
                    color: '#1f7a3f',
                    marginBottom: '12px',
                  }}
                >
                  <span>
                    {cart.referral
                      ? `Referral (${cart.referral.code.toUpperCase()})`
                      : `Discount (${cart.coupon!.code.toUpperCase()})`}
                  </span>
                  <span>&minus;${cart.discount.toFixed(2)}</span>
                </div>
              ) : null}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
              >
                <span>Total</span>
                <span style={{ color: '#004d71' }}>${cart.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Referral code — a GeauxCounsel member's code gives the client a
                discount and credits the member. Auto-filled from a ?ref= link. */}
            <div style={{ marginBottom: '20px' }}>
              {cart.referral ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '14px',
                    color: '#1f7a3f',
                  }}
                >
                  <span>
                    Referral <strong>{cart.referral.code.toUpperCase()}</strong> applied — {cart.referral.refereeDiscountPercent}% off
                  </span>
                  <button
                    type="button"
                    onClick={() => removeReferral()}
                    style={{ background: 'none', border: 'none', color: '#004d71', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={refInput}
                    onChange={(e) => setRefInput(e.target.value)}
                    placeholder="Referral code (optional)"
                    style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '16px' }}
                  />
                  <button
                    type="button"
                    onClick={() => { if (refInput.trim()) applyReferral(refInput.trim()); }}
                    className="btn btn-outline-primary"
                    style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Checkout Button or Login Prompt */}
            {MAINTENANCE_MODE && !testMode ? (
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '25px',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center',
                }}
              >
                <button
                  disabled
                  className="btn btn-secondary btn-lg"
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '18px',
                    cursor: 'not-allowed',
                    opacity: 0.6,
                  }}
                >
                  <i className="fas fa-pause-circle me-2"></i>
                  Checkout Temporarily Unavailable
                </button>
                <p
                  style={{
                    textAlign: 'center',
                    marginTop: '15px',
                    fontSize: '14px',
                    color: '#707070',
                  }}
                >
                  Please check back soon. We appreciate your patience!
                </p>
              </div>
            ) : authLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Loading...</p>
              </div>
            ) : isAuthenticated ? (
              <>
                <button
                  onClick={handleCheckout}
                  disabled={isLoading}
                  className="btn btn-primary btn-lg"
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '18px',
                  }}
                >
                  {isLoading ? (
                    'Processing...'
                  ) : (
                    <>
                      <i className="fas fa-lock me-2"></i>
                      Proceed to Secure Payment
                    </>
                  )}
                </button>

                <p
                  style={{
                    textAlign: 'center',
                    marginTop: '15px',
                    fontSize: '14px',
                    color: '#707070',
                  }}
                >
                  <i className="fas fa-shield-alt me-1"></i>
                  Secure payment powered by Stripe
                </p>
              </>
            ) : (
              <div
                style={{
                  border: '1px solid #e3f2fd',
                  borderRadius: '8px',
                  padding: '25px',
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center',
                }}
              >
                <h3 style={{ marginBottom: '10px', fontSize: '18px' }}>
                  <i className="fas fa-user-circle me-2"></i>
                  Account Required
                </h3>
                <p style={{ color: '#555', marginBottom: '20px' }}>
                  Please log in or create an account to complete your purchase. Your documents will be saved to your account for easy access.
                </p>
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                  <Link
                    to={`/login?redirect=${encodeURIComponent(returnUrl)}`}
                    className="btn btn-primary"
                    style={{ padding: '12px 30px' }}
                  >
                    Log In
                  </Link>
                  <Link
                    to={`/register?redirect=${encodeURIComponent(returnUrl)}`}
                    className="btn btn-outline-primary"
                    style={{ padding: '12px 30px' }}
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            )}

            {/* Back Link */}
            <p style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#004d71',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                &larr; Go Back
              </button>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Checkout;
